import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { INQUIRY_STATUS, InquiryPatchInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const Inquiry = z.object({
  id: z.string().uuid(),
  listing_id: z.string().uuid().nullable(),
  reason: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  lang: z.string(),
  message: z.string(),
  status: z.enum(INQUIRY_STATUS),
  assigned_to: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Inquiry = z.infer<typeof Inquiry>;

const InquiriesResponse = z.object({
  items: z.array(Inquiry),
  total: z.number(),
  page: z.number(),
  has_more: z.boolean(),
});

export function useInquiries(filters: { status?: string; listing_id?: string } = {}) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["inquiries", filters],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/inquiries", token!, InquiriesResponse, {
      query: { status: filters.status, listing_id: filters.listing_id, page_size: 100 },
    }),
  });
}

const InquiryPatchResponse = z.object({
  id: z.string().uuid(),
  status: z.enum(INQUIRY_STATUS),
  assigned_to: z.string().uuid().nullable(),
  updated_at: z.string(),
});

export function usePatchInquiry() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: z.infer<typeof InquiryPatchInput> }) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/inquiries/${id}`, token, InquiryPatchResponse, {
        method: "PATCH", body: patch,
      });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["inquiries"] }); },
  });
}
